/* 3D globe canvas — the react-globe.gl renderer originally inside GlobeView.
   Pure presentation: all state (payload, filters, adoption, hovered country)
   is owned by the parent WorldView container.

   Rendering model (upgraded — bespoke WebGL centrepiece):
     - incidents  → custom GLSL "risk columns" (vertical energy pillars whose
                    height + colour encode severity) on react-globe.gl's
                    customLayerData; new high-severity ones also fire a one-shot
                    expanding ripple ring.
     - flights / ships / fleet → `htmlElementsData` (inline SVG icons, clickable)
     - per-asset trails → `pathsData` (animated dashed threads)
     - arcs       → animated great-circle arcs with a travelling light pulse,
                    coloured by kind.
     - atmosphere → a hand-written fresnel shader shell (cyan rim glow).
     - globe body → a dark-earth MeshPhongMaterial with a faint cyan emissive.

   Motion respects `prefers-reduced-motion`: no idle auto-rotate, and every
   shader's animated term is frozen via setReducedMotion().
*/
import { useEffect, useMemo, useRef } from "react"
import * as THREE from "three"
import Globe from "react-globe.gl"
import { type CountryFeature } from "./countries"
import { GLOBE_PALETTE } from "./globe-colors"
import { numericIdToAlpha2 } from "./iso-codes"
import { useRegion } from "./store"
import type { TrailItem } from "./useAssetTrails"
import type { GlobeArc, GlobePoint } from "./types"
import {
  type AtmosphereHandle,
  type CustomDatum,
  type RippleDatum,
  buildAtmosphere,
  buildColumnData,
  customLayerLabel,
  isHighSeverity,
  makeCustomObject,
  setReducedMotion,
  tickClock,
  updateCustomObject,
} from "./globe/customLayers"
import { buildGlobeMaterial } from "./globe/globeMaterial"

interface Props {
  width: number
  height: number
  points: GlobePoint[]
  arcs: GlobeArc[]
  countries: CountryFeature[]
  showCountries: boolean
  hoveredCountry: CountryFeature | null
  onHoverCountry: (c: CountryFeature | null) => void
  onPointClick: (p: GlobePoint) => void
  trails: TrailItem[]
}

// --- Icon CSS (injected once per document) --------------------------------

const ICON_STYLE_ID = "supplylens-globe-icon-styles"
const ICON_STYLE_CSS = `
.sl-globe-icon{
  position:relative;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  pointer-events:auto;
  cursor:pointer;
  color:#a5f3fc;
  transition:transform 0.12s ease-out, filter 0.12s ease-out;
  filter:drop-shadow(0 0 3px rgba(0,0,0,0.65));
  will-change:transform;
}
.sl-globe-icon svg{display:block;width:100%;height:100%;fill:currentColor;stroke:none;}
.sl-globe-icon:hover{transform:scale(1.45);filter:drop-shadow(0 0 6px currentColor);}
.sl-globe-icon--owned{color:#facc15;}
.sl-globe-icon__rot{display:block;width:100%;height:100%;transition:transform 0.3s linear;}
`

function ensureIconStyles() {
  if (typeof document === "undefined") return
  if (document.getElementById(ICON_STYLE_ID)) return
  const style = document.createElement("style")
  style.id = ICON_STYLE_ID
  style.textContent = ICON_STYLE_CSS
  document.head.appendChild(style)
}

// Simple hand-drafted silhouettes. All use `fill: currentColor` so the
// container's inline `color` style drives the swatch.
const AIRPLANE_SVG =
  '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
  '<path d="M10 1.2c-.45 0-.82.37-.82.82v5.3L2 11.5v1.6l7.18-2.1v4.2l-1.9 1.2v1.3L10 17l2.72.7v-1.3l-1.9-1.2v-4.2l7.18 2.1v-1.6L10.82 7.3V2.02c0-.45-.37-.82-.82-.82Z"/>' +
  "</svg>"

const SHIP_SVG =
  '<svg viewBox="0 0 22 12" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
  '<path d="M9 1h4v2h2v1h3l-2 4H6L4 4h3V3h2V1Zm-7 7h18l-2 3H4L2 8Z"/>' +
  "</svg>"

const DIAMOND_SVG =
  '<svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
  '<path d="M7 1l6 6-6 6-6-6 6-6Z"/>' +
  "</svg>"

type IconShape = "airplane" | "ship" | "diamond"

function pickShape(p: GlobePoint): IconShape {
  if (p.kind === "flight") return "airplane"
  if (p.kind === "ship") return "ship"
  // fleet: look at kind_detail for a finer pick, default diamond
  if (p.kind === "fleet") {
    const detail = (p.kind_detail ?? "").toLowerCase()
    if (detail.includes("air") || detail === "aircraft" || detail === "flight") {
      return "airplane"
    }
    if (
      detail.includes("vessel") ||
      detail.includes("ship") ||
      detail.includes("barge") ||
      detail.includes("boat")
    ) {
      return "ship"
    }
    return "diamond"
  }
  return "diamond"
}

function shapeSvg(shape: IconShape): string {
  if (shape === "airplane") return AIRPLANE_SVG
  if (shape === "ship") return SHIP_SVG
  return DIAMOND_SVG
}

function shapeSizePx(shape: IconShape, p: GlobePoint): { w: number; h: number } {
  if (shape === "airplane") {
    const base = p.kind === "fleet" ? 16 : 10
    return { w: base, h: base }
  }
  if (shape === "ship") {
    const base = p.kind === "fleet" ? 18 : 13
    // native ratio ≈ 22×12
    return { w: base, h: Math.round((base * 12) / 22) }
  }
  return { w: 12, h: 12 }
}

function defaultColor(p: GlobePoint): string {
  if (p.owned) return GLOBE_PALETTE.fleet
  if (p.kind === "flight") return GLOBE_PALETTE.flight
  if (p.kind === "ship") return GLOBE_PALETTE.ship
  return p.color || "#94a3b8"
}

function tooltipText(d: GlobePoint): string {
  const parts = [d.label, d.kind.toUpperCase()]
  if (d.owned) parts.push("★ owned")
  if (d.altitude_m != null) parts.push(`${Math.round(d.altitude_m / 1000)}km`)
  if (d.velocity_kt != null) parts.push(`${Math.round(d.velocity_kt)}kt`)
  if (d.heading != null) parts.push(`${Math.round(d.heading)}°`)
  return parts.join(" · ")
}

// Build the element passed to react-globe.gl's HTML layer for each asset.
// Globe.gl attaches this div to the canvas overlay; we handle our own click
// because the library has no equivalent of onPointClick for htmlElementsData.
function buildIconElement(
  d: GlobePoint,
  onAssetClick: (p: GlobePoint) => void,
): HTMLDivElement {
  const shape = pickShape(d)
  const { w, h } = shapeSizePx(shape, d)

  const wrapper = document.createElement("div")
  wrapper.className = "sl-globe-icon" + (d.owned ? " sl-globe-icon--owned" : "")
  wrapper.style.width = `${w}px`
  wrapper.style.height = `${h}px`
  wrapper.style.color = defaultColor(d)
  wrapper.title = tooltipText(d)
  wrapper.dataset.assetId = d.id

  const rot = document.createElement("span")
  rot.className = "sl-globe-icon__rot"
  // Rotate airplane icons to match heading (fleet aircraft too when we
  // know the bearing). Ship/diamond stay upright.
  if (shape === "airplane" && typeof d.heading === "number") {
    rot.style.transform = `rotate(${d.heading}deg)`
  }
  rot.innerHTML = shapeSvg(shape)
  wrapper.appendChild(rot)

  wrapper.addEventListener("click", (ev) => {
    ev.stopPropagation()
    onAssetClick(d)
  })

  return wrapper
}

// --- Reduced-motion detection ---------------------------------------------

function usePrefersReducedMotion(): boolean {
  const ref = useRef(false)
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    ref.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
  return ref.current
}

// --- Component ------------------------------------------------------------

export function GlobeCanvas({
  width,
  height,
  points,
  arcs,
  countries,
  showCountries,
  hoveredCountry,
  onHoverCountry,
  onPointClick,
  trails,
}: Props) {
  const globeRef = useRef<any>(null)
  const openRegion = useRegion((s) => s.openRegion)
  const onPointClickRef = useRef(onPointClick)
  onPointClickRef.current = onPointClick

  const reducedMotion = usePrefersReducedMotion()

  // Keep the shared shader clock aware of the motion preference.
  useEffect(() => {
    setReducedMotion(reducedMotion)
  }, [reducedMotion])

  // One stable globe material instance for the body (dark earth + cyan glow).
  const globeMaterial = useMemo(() => buildGlobeMaterial(), [])

  // Split render domains: incidents now become custom GLSL risk columns;
  // flights/ships/fleet go to the HTML icon layer. pointsData stays empty so
  // the built-in point spheres never compete with our columns.
  const { iconData, incidentData } = useMemo(() => {
    const ic: GlobePoint[] = []
    const inc: GlobePoint[] = []
    for (const p of points) {
      if (p.kind === "incident") inc.push(p)
      else ic.push(p)
    }
    return { iconData: ic, incidentData: inc }
  }, [points])

  // Risk-column custom data, rebuilt when the incident set changes.
  const columnData = useMemo(() => buildColumnData(incidentData), [incidentData])

  // Ripple rings: a short-lived overlay we manage imperatively. When a new
  // high-severity incident id appears we push a ripple datum that ages out.
  const seenIncidentIds = useRef<Set<string>>(new Set())
  const ripplesRef = useRef<RippleDatum[]>([])
  const firstIncidentPass = useRef(true)

  // Combined custom layer = columns + active ripples. We recompute on every
  // render of the parent so aged-out ripples disappear; the per-frame tick in
  // onGlobeReady triggers re-renders implicitly via globe's own animation loop.
  const customData = useMemo<CustomDatum[]>(() => {
    const now = Date.now()
    // Detect newly-appeared high-severity incidents → spawn ripples.
    const next = new Set<string>()
    for (const p of incidentData) {
      next.add(p.id)
      if (!seenIncidentIds.current.has(p.id) && isHighSeverity(p)) {
        // Skip the very first payload so we don't ripple the whole world on load.
        if (!firstIncidentPass.current) {
          ripplesRef.current.push({
            type: "ripple",
            id: `ripple:${p.id}:${now}`,
            lat: p.lat,
            lng: p.lng,
            size: p.severity === "critical" ? 0.16 : 0.12,
            color: p.severity === "critical" ? GLOBE_PALETTE.incident : GLOBE_PALETTE.incidentHigh,
            bornMs: now,
            lifeMs: 2600,
          })
        }
      }
    }
    seenIncidentIds.current = next
    firstIncidentPass.current = false
    // Age out expired ripples.
    ripplesRef.current = ripplesRef.current.filter((r) => now - r.bornMs < r.lifeMs)
    return [...columnData, ...ripplesRef.current]
  }, [columnData, incidentData])

  // Lookup from id → freshest point, used by the trail click handler so a
  // click on a thread opens the inspector on the matching asset metadata.
  const pointsById = useMemo(() => {
    const m = new Map<string, GlobePoint>()
    for (const p of points) m.set(p.id, p)
    return m
  }, [points])

  // Incident lookup so a click on a risk column opens the right inspector.
  const incidentById = useMemo(() => {
    const m = new Map<string, GlobePoint>()
    for (const p of incidentData) m.set(p.id, p)
    return m
  }, [incidentData])

  useEffect(() => {
    ensureIconStyles()
  }, [])

  // --- Atmosphere + idle auto-rotate + shared clock -----------------------
  // We drive our shader-time updates from a self-owned requestAnimationFrame
  // loop (NOT renderer.setAnimationLoop) so we never interfere with globe.gl's
  // own render loop, which runs on three-render-objects' internal rAF.
  const atmosphereRef = useRef<AtmosphereHandle | null>(null)
  const lastInteractionRef = useRef<number>(Date.now())
  const rafRef = useRef<number | null>(null)
  const reducedMotionRef = useRef(reducedMotion)
  reducedMotionRef.current = reducedMotion

  const handleGlobeReady = () => {
    const g = globeRef.current
    if (!g) return

    // Inject the bespoke fresnel atmosphere shell into the scene once.
    if (!atmosphereRef.current && typeof g.getGlobeRadius === "function") {
      const radius = g.getGlobeRadius()
      const atmo = buildAtmosphere(radius)
      atmosphereRef.current = atmo
      try {
        g.scene().add(atmo.mesh)
      } catch {
        /* scene not ready — skip; harmless */
      }
    }

    // Controls: damped, with a gentle idle auto-rotate (disabled if reduced).
    if (typeof g.controls === "function") {
      const ctrls = g.controls()
      ctrls.enableDamping = true
      ctrls.dampingFactor = 0.08
      ctrls.autoRotate = !reducedMotionRef.current
      ctrls.autoRotateSpeed = 0.18
      ctrls.addEventListener?.("start", () => {
        lastInteractionRef.current = Date.now()
        ctrls.autoRotate = false
      })
      ctrls.addEventListener?.("end", () => {
        lastInteractionRef.current = Date.now()
      })
    }

    // Start our own per-frame loop: advance the shared shader clock, breathe the
    // atmosphere, and re-arm idle auto-rotate after the user stops interacting.
    if (rafRef.current == null) {
      const frame = () => {
        const reduced = reducedMotionRef.current
        const t = reduced ? 0 : tickClock()
        atmosphereRef.current?.update(t)
        if (!reduced && globeRef.current && typeof globeRef.current.controls === "function") {
          const ctrls = globeRef.current.controls()
          if (!ctrls.autoRotate && Date.now() - lastInteractionRef.current > 4000) {
            ctrls.autoRotate = true
          }
        }
        rafRef.current = requestAnimationFrame(frame)
      }
      rafRef.current = requestAnimationFrame(frame)
    }
  }

  useEffect(() => {
    return () => {
      // Tear down our rAF loop + atmosphere on unmount.
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      const g = globeRef.current
      try {
        if (atmosphereRef.current && g && typeof g.scene === "function") {
          g.scene().remove(atmosphereRef.current.mesh)
        }
      } catch {
        /* ignore teardown races */
      }
      atmosphereRef.current?.dispose()
      atmosphereRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!globeRef.current) return
    globeRef.current.pointOfView({ lat: 25, lng: 40, altitude: 2.4 }, 0)
  }, [])

  return (
    <Globe
      ref={globeRef}
      width={width}
      height={height}
      backgroundColor={GLOBE_PALETTE.bg}
      globeMaterial={globeMaterial}
      onGlobeReady={handleGlobeReady}
      showAtmosphere={false} // replaced by our custom fresnel shell
      // No built-in point spheres: incidents render as custom risk columns.
      pointsData={[]}
      htmlElementsData={iconData}
      htmlLat={(d: any) => (d as GlobePoint).lat}
      htmlLng={(d: any) => (d as GlobePoint).lng}
      htmlAltitude={(d: any) => {
        const p = d as GlobePoint
        if (p.kind === "flight") {
          return Math.min(0.22, (p.altitude_m ?? 10000) / 55000)
        }
        if (p.kind === "fleet") return 0.03
        return 0.015
      }}
      htmlElement={(d: object) =>
        buildIconElement(d as GlobePoint, (p) => onPointClickRef.current(p))
      }
      // --- Custom GLSL layer: incident risk columns + ripple rings ---------
      customLayerData={customData as unknown as object[]}
      customThreeObject={(d: object) => makeCustomObject(d)}
      customThreeObjectUpdate={(obj: object, d: object) => {
        if (globeRef.current) {
          updateCustomObject(obj as THREE.Object3D, d, globeRef.current)
        }
      }}
      customLayerLabel={(d: object) => customLayerLabel(d)}
      onCustomLayerClick={(d: object) => {
        const datum = d as CustomDatum
        if (datum.type === "column") {
          const match = incidentById.get(datum.id)
          if (match) onPointClickRef.current(match)
        }
      }}
      // --- Animated great-circle arcs with travelling pulse ----------------
      arcsData={arcs}
      arcStartLat={(a: any) => (a as GlobeArc).from_lat}
      arcStartLng={(a: any) => (a as GlobeArc).from_lng}
      arcEndLat={(a: any) => (a as GlobeArc).to_lat}
      arcEndLng={(a: any) => (a as GlobeArc).to_lng}
      arcColor={(a: any) => arcGradient(a as GlobeArc)}
      arcLabel={(a: any) => arcHoverHtml(a as GlobeArc)}
      arcStroke={0.5}
      // Short bright dash + long gap = a discrete light pulse travelling the lane.
      arcDashLength={reducedMotion ? 1 : 0.32}
      arcDashGap={reducedMotion ? 0 : 1.4}
      arcDashInitialGap={(a: any) => arcStagger(a as GlobeArc)}
      arcDashAnimateTime={reducedMotion ? 0 : 2600}
      arcAltitudeAutoScale={0.45}
      arcsTransitionDuration={400}
      pathsData={trails}
      pathPoints={(t: any) => (t as TrailItem).coords}
      pathPointLat={(pt: any) => (pt as [number, number])[0]}
      pathPointLng={(pt: any) => (pt as [number, number])[1]}
      pathPointAlt={0.004}
      pathColor={(t: any) =>
        (t as TrailItem).owned
          ? "rgba(250,204,21,0.75)"
          : "rgba(180,180,180,0.55)"
      }
      pathStroke={0.15}
      pathDashLength={0.25}
      pathDashGap={0.5}
      pathDashAnimateTime={reducedMotion ? 0 : 3200}
      pathTransitionDuration={0}
      pathLabel={(t: any) => trailHoverHtml(t as TrailItem, pointsById)}
      onPathClick={(t: any) => {
        const trail = t as TrailItem
        const match = pointsById.get(trail.id)
        if (match) onPointClickRef.current(match)
      }}
      polygonsData={showCountries ? countries : []}
      // react-globe.gl's bundled `GeoJsonGeometry` typing is imprecise
      // (`coordinates: number[]`), so we cast the accessor return.
      polygonGeoJsonGeometry={((p: any) =>
        (p as CountryFeature).geometry) as unknown as (o: object) => any}
      polygonCapColor={(p: any) =>
        p === hoveredCountry ? "rgba(34,211,238,0.18)" : "rgba(51,65,85,0.22)"
      }
      polygonSideColor={() => "rgba(34,211,238,0.05)"}
      polygonStrokeColor={() => "rgba(34,211,238,0.42)"}
      polygonAltitude={(p: any) => (p === hoveredCountry ? 0.012 : 0.006)}
      polygonLabel={(p: any) => countryHoverHtml(p as CountryFeature)}
      onPolygonHover={(p: any) => onHoverCountry((p as CountryFeature | null) ?? null)}
      onPolygonClick={(p: any) => {
        const alpha2 = numericIdToAlpha2((p as CountryFeature).id)
        if (alpha2) openRegion(alpha2)
      }}
      polygonsTransitionDuration={150}
      enablePointerInteraction={true}
      // Trail strokes render thin; widen the hit region so clicks on a
      // grey thread reliably pick the underlying path.
      lineHoverPrecision={1.2}
    />
  )
}

// Stagger arc pulses by hashing the id so lanes don't all blink in lockstep.
function arcStagger(arc: GlobeArc): number {
  let h = 0
  for (let i = 0; i < arc.id.length; i++) h = (h * 31 + arc.id.charCodeAt(i)) | 0
  return (Math.abs(h) % 100) / 100
}

// Color gradient stops per arc kind. Transparent→saturated gives the animated
// "flow" look along the great-circle path. Colours mirror the palette:
// fleet/shipment #facc15, disrupted #ef4444 (incident), default #22d3ee (accent).
function arcGradient(arc: GlobeArc): [string, string, string] {
  if (arc.kind === "disrupted") {
    // incident red — bright pulse head, fading tail.
    return ["rgba(239,68,68,0.05)", "rgba(239,68,68,0.55)", "rgba(239,68,68,1.0)"]
  }
  if (arc.owned || arc.kind === "shipment") {
    return ["rgba(250,204,21,0.05)", "rgba(250,204,21,0.5)", "rgba(250,204,21,1.0)"]
  }
  return ["rgba(34,211,238,0.04)", "rgba(34,211,238,0.45)", "rgba(34,211,238,0.95)"]
}

function arcHoverHtml(a: GlobeArc): string {
  const esc = (s: string) =>
    s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" })[c] ?? c)
  const kindColor =
    a.kind === "disrupted" ? "#ef4444" : a.kind === "shipment" || a.owned ? "#facc15" : "#22d3ee"
  const owned = a.owned ? ' <span style="color:#facc15">★</span>' : ""
  const kindLabel = a.kind === "trade-route" ? "TRADE ROUTE" : a.kind.toUpperCase()
  return `<div style="font:600 11px ui-monospace,SF Mono,monospace;background:rgba(15,20,25,0.95);border:1px solid #2a2a2a;border-radius:3px;padding:5px 9px;color:#e8e8e8;white-space:nowrap">${esc(a.label)}${owned} <span style="color:${kindColor};text-transform:uppercase;letter-spacing:0.1em">· ${kindLabel}</span></div>`
}

function trailHoverHtml(t: TrailItem, byId: Map<string, GlobePoint>): string {
  const esc = (s: string) =>
    s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" })[c] ?? c)
  const match = byId.get(t.id)
  const label = match?.label ?? t.id
  const owned = t.owned ? ' <span style="color:#facc15">★</span>' : ""
  const kind = t.kind.toUpperCase()
  return `<div style="font:600 11px ui-monospace,SF Mono,monospace;background:rgba(15,20,25,0.95);border:1px solid #2a2a2a;border-radius:3px;padding:5px 9px;color:#e8e8e8;white-space:nowrap">${esc(label)} · ${kind}${owned}</div>`
}

function countryHoverHtml(p: CountryFeature): string {
  const esc = (s: string) =>
    s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" })[c] ?? c)
  const name = p.properties?.name ?? "Unknown region"
  const alpha2 = numericIdToAlpha2(p.id)
  const tag = alpha2
    ? ` <span style="color:#22d3ee;letter-spacing:0.1em">· ${alpha2}</span>`
    : ' <span style="color:#64748b;letter-spacing:0.1em">· —</span>'
  return `<div style="font:600 11px ui-monospace,SF Mono,monospace;background:rgba(15,20,25,0.95);border:1px solid #2a2a2a;border-radius:3px;padding:5px 9px;color:#e8e8e8;white-space:nowrap">${esc(name)}${tag}</div>`
}
